import { getApp } from '@lingoda/env';
import { extractMessageFromError, getErrorContext } from '@lingoda/errors';
import { isMonitorDebugLogsEnabled } from '@lingoda/local-feature-flags';
import { fetchWithRetry, maskToken } from '@lingoda/utils';
import { getLogScope, getLogsConfig } from './logScope';
import type { LogContext } from './logScope';

export const logInfo = (title: string, context?: LogContext): void => {
    if (isMonitorDebugLogsEnabled()) {
        console.info(title, ...(context ? [context] : []));
    }
    sendLog('info', title, context);
};

export const logDebugError = (error: unknown, context?: LogContext): void => {
    const message = extractMessageFromError(error) ?? 'Unknown debug message';
    const errorContext = getErrorContext(error);
    console.warn(message);

    if (errorContext) {
        console.debug(errorContext);
    }

    sendLog('debug', message, {
        ...context,
        ...errorContext,
        ...(error instanceof Error && { errorName: error.name, stack: error.stack }),
    });
};

// Logs only, without the console call (the error tracker turns console calls into breadcrumbs)
export const sendLog = (level: 'debug' | 'info', message: string, context?: LogContext): void => {
    try {
        const logsConfig = getLogsConfig();

        if (!logsConfig) {
            return;
        }

        const scope = getLogScope();
        const body = {
            meta: {
                app: {
                    name: getApp(),
                    release: logsConfig.releaseId,
                    environment: logsConfig.deployEnvironment,
                } satisfies Record<string, string>,
                user: scope.user && {
                    id: String(scope.user.id),
                    attributes: {
                        uid: scope.user.uid,
                        role: scope.user.role,
                    } satisfies Record<string, string>,
                },
                page: { url: maskToken(window.location.href) },
            },
            logs: [
                {
                    // Browser time, kept in the line for information only. Alloy stamps the Loki entry
                    // with its own server time on arrival, so a wrong device clock does not move the log.
                    timestamp: new Date().toISOString(),
                    level,
                    message,
                    context: {
                        payload: JSON.stringify({ ...scope.context, ...context }),
                    } satisfies Record<string, string>,
                },
            ],
        };

        fetchWithRetry({
            url: logsConfig.endpoint,
            options: {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-API-Key': logsConfig.apiKey },
                body: JSON.stringify(body),
            },
        }).catch((fetchError: unknown) => {
            console.error(
                'Sending log failed',
                fetchError instanceof Response ? fetchError.status : fetchError,
            );
        });
    } catch (buildError) {
        console.error('Building log failed', buildError);
    }
};
